import React from "react";
import { AlertCircle, CheckCircle2, Clock, HeartHandshake } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import MHLogoBlack from "../../assets/Icons/MHLogoBlack";
import { SUPPORT_EMAIL, SUPPORT_PHONE, vkycStyles as styles } from "./styles";

export const KYC_TRUST_CHIPS = [
  "Encrypted session",
  "RBI complaint",
  "Agent-assisted verification",
] as const;

type StatusTone = "expired" | "error" | "success" | "thanks";

const STATUS_ICON: Record<StatusTone, React.ReactNode> = {
  expired: <Clock className="h-7 w-7 text-amber-600" />,
  error: <AlertCircle className="h-7 w-7 text-red-500" />,
  success: <CheckCircle2 className="h-7 w-7 text-green-600" />,
  thanks: <HeartHandshake className="h-7 w-7 text-primary" />,
};

const STATUS_ICON_BG: Record<StatusTone, string> = {
  expired: "bg-[#FFF8E7]",
  error: "bg-red-50",
  success: "bg-[#ECFDF3]",
  thanks: "bg-[#ECF2FF]",
};

export const PublicKycHeader: React.FC = () => (
  <header className={styles.header}>
    <div className={styles.headerLogo}>
      <MHLogoBlack fillColor="h-full w-full" />
    </div>
    <span className={styles.headerEncrypted}>Encrypted</span>
    <span className="hidden rounded-full bg-[#ECF2FF] px-2.5 py-1 text-[11px] font-semibold text-primary md:inline-flex lg:hidden">
      Secure
    </span>
    <span className="hidden rounded-full bg-[#ECF2FF] px-2.5 py-1 text-[11px] font-semibold text-primary lg:inline-flex">
      Secure verification
    </span>
  </header>
);

export const SupportBlock: React.FC = () => (
  <div className={styles.supportBox}>
    <p className={styles.supportLabel}>Please reach out to support in case of help</p>
    <a className={styles.supportLink} href={`tel:${SUPPORT_PHONE}`}>
      {SUPPORT_PHONE}
    </a>
    <a className={styles.supportLink} href={`mailto:${SUPPORT_EMAIL}`}>
      {SUPPORT_EMAIL}
    </a>
  </div>
);

export const TrustRail: React.FC<{ chips?: readonly string[] }> = ({
  chips = KYC_TRUST_CHIPS,
}) => (
  <div className={styles.trustRail}>
    {chips.map((chip) => (
      <span key={chip} className={styles.trustChip}>
        {chip}
      </span>
    ))}
  </div>
);

export const PublicKycShell: React.FC<{
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ children, footer }) => (
  <div className={styles.page}>
    <PublicKycHeader />
    <div className={styles.scroll}>{children}</div>
    {footer}
  </div>
);

export const PublicKycLoading: React.FC<{ message: string; hint?: string }> = ({
  message,
  hint = "Please do not close this window.",
}) => (
  <PublicKycShell>
    <div className={styles.body}>
      <div className={styles.loadingCard}>
        <div className={styles.spinner} />
        <p className="mt-1 text-sm text-[#64748B]">{message}</p>
        <p className="text-xs text-[#94A3B8]">{hint}</p>
      </div>
    </div>
  </PublicKycShell>
);

interface StatusLayoutProps {
  eyebrow?: string;
  contextTitle: string;
  contextBody: string;
  tips?: string[];
  tone: StatusTone;
  statusTitle: string;
  statusDescription?: string;
  note?: React.ReactNode;
  action?: React.ReactNode;
  showSupport?: boolean;
  showTrust?: boolean;
}

export const PublicKycStatusLayout: React.FC<StatusLayoutProps> = ({
  eyebrow = "VIDEO KYC",
  contextTitle,
  contextBody,
  tips = [],
  tone,
  statusTitle,
  statusDescription,
  note,
  action,
  showSupport = true,
  showTrust = true,
}) => (
  <PublicKycShell>
    <div className={styles.body}>
      <div className={styles.stage}>
        <section className={styles.contextCard}>
          <p className={styles.eyebrow}>{eyebrow}</p>
          <h1 className={styles.contextTitle}>{contextTitle}</h1>
          <p className={styles.contextBody}>{contextBody}</p>
          {tips.length ? (
            <div className={styles.tipList}>
              {tips.map((tip, index) => (
                <div key={`${index}-${tip}`} className={styles.tipRow}>
                  <span className={styles.tipNum}>{index + 1}</span>
                  <p className={styles.tipText}>{tip}</p>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <section className={styles.statusCard}>
          <div className={`${styles.statusIcon} ${STATUS_ICON_BG[tone]}`}>{STATUS_ICON[tone]}</div>
          <h2 className={styles.statusTitle}>{statusTitle}</h2>
          {statusDescription ? <p className={styles.statusDescription}>{statusDescription}</p> : null}
          {note}
          {showSupport ? <SupportBlock /> : null}
          {action}
        </section>
      </div>
      {showTrust ? <TrustRail /> : null}
    </div>
  </PublicKycShell>
);

export interface PublicKycLandingStep {
  title: string;
  mobileTitle?: string;
  description: string;
}

export interface PublicKycKeepItem {
  label: string;
  hint?: string;
  icon: LucideIcon;
}

interface PublicKycLandingProps {
  eyebrow: string;
  title: string;
  subtitleDesktop: string;
  subtitleCompact: string;
  steps: PublicKycLandingStep[];
  keepItems: PublicKycKeepItem[];
  keepTitleDesktop?: string;
  keepTitleMobile?: string;
  journeyTitle?: string;
  ctaLabel: string;
  ctaBusy?: boolean;
  ctaDisabled?: boolean;
  onCta: () => void;
  notice?: React.ReactNode;
  legalDesktop?: string;
  legalMobile?: string;
}

export const PublicKycLanding: React.FC<PublicKycLandingProps> = ({
  eyebrow,
  title,
  subtitleDesktop,
  subtitleCompact,
  steps,
  keepItems,
  keepTitleDesktop = "Here is what to keep handy",
  keepTitleMobile = "Keep handy",
  journeyTitle = "Your journey",
  ctaLabel,
  ctaBusy,
  ctaDisabled,
  onCta,
  notice,
  legalDesktop = "By proceeding, you agree to our verification terms and privacy policy.",
  legalMobile = "By proceeding, you agree to verification terms and privacy policy.",
}) => {
  const cta = (
    <div className={styles.ctaWrap}>
      {notice}
      <button
        type="button"
        onClick={onCta}
        disabled={ctaBusy || ctaDisabled}
        className={styles.acceptBtn}
      >
        {ctaLabel}
      </button>
      <p className={styles.legalNote}>
        <span className="md:hidden">{legalMobile}</span>
        <span className="hidden md:inline">{legalDesktop}</span>
      </p>
    </div>
  );

  return (
    <PublicKycShell footer={<div className={`${styles.stickyCta} md:hidden`}>{cta}</div>}>
      <div className={styles.body}>
        <div className={styles.hero}>
          <p className={styles.eyebrow}>{eyebrow}</p>
          <h1 className={styles.heroTitle}>{title}</h1>
          <p className={styles.heroSubtitle}>
            <span className="lg:hidden">{subtitleCompact}</span>
            <span className="hidden lg:inline">{subtitleDesktop}</span>
          </p>
        </div>

        <div className={styles.compactSteps}>
          {steps.map((step, index) => {
            const isActive = index === 0;
            return (
              <div
                key={step.title}
                className={isActive ? styles.compactStepActive : styles.compactStepIdle}
              >
                <div className={styles.compactStepTop}>
                  <span className={isActive ? styles.compactNumActive : styles.compactNumIdle}>
                    {index + 1}
                  </span>
                  <span className={isActive ? styles.stepBadgeNow : styles.stepBadgeNext}>
                    {isActive ? (
                      <>
                        <span className="md:hidden">Starts now</span>
                        <span className="hidden md:inline lg:hidden">Now</span>
                      </>
                    ) : (
                      <>
                        <span className="md:hidden">Up next</span>
                        <span className="hidden md:inline lg:hidden">Next</span>
                      </>
                    )}
                  </span>
                </div>
                <p className={styles.compactStepTitle}>
                  <span className="md:hidden">{step.mobileTitle || step.title}</span>
                  <span className="hidden md:inline">{step.title}</span>
                </p>
              </div>
            );
          })}
        </div>

        <div className={styles.landingGrid}>
          <section className={styles.journeyCard}>
            <p className={styles.journeyTitle}>{journeyTitle}</p>
            {steps.map((step, index) => {
              const isActive = index === 0;
              return (
                <div key={step.title} className={isActive ? styles.stepActive : styles.stepIdle}>
                  <span className={isActive ? styles.stepNumActive : styles.stepNumIdle}>
                    {index + 1}
                  </span>
                  <div className={styles.stepCopy}>
                    <p className={styles.stepTitle}>{step.title}</p>
                    <p className={styles.stepDesc}>{step.description}</p>
                  </div>
                  <span className={isActive ? styles.stepBadgeNow : styles.stepBadgeNext}>
                    {isActive ? "Starts now" : "Up next"}
                  </span>
                </div>
              );
            })}
          </section>

          <section className={styles.keepCard}>
            <p className={styles.keepTitle}>
              <span className="md:hidden">{keepTitleMobile}</span>
              <span className="hidden md:inline">{keepTitleDesktop}</span>
            </p>
            {keepItems.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className={styles.keepItem}>
                  <span className={styles.keepIcon}>
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2} />
                  </span>
                  <div>
                    <p className={styles.keepLabel}>{item.label}</p>
                    {item.hint ? <p className={styles.keepHint}>{item.hint}</p> : null}
                  </div>
                </div>
              );
            })}
          </section>
        </div>

        <div className="hidden md:block">{cta}</div>
      </div>
    </PublicKycShell>
  );
};
